import React, { useEffect, useRef, useState } from 'react';
import { Loader2, VideoOff } from 'lucide-react';
import { getAssetUrl } from '../../services/api';
import { readMjpeg } from './mjpegStream';

async function decodeFrame(blob) {
  if (typeof createImageBitmap === 'function') return createImageBitmap(blob);
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function VideoAbertura({ dispositivoId, nome }) {
  const container = useRef(null);
  const canvas = useRef(null);
  const [visible, setVisible] = useState(true);
  const [pageVisible, setPageVisible] = useState(!document.hidden);
  const [status, setStatus] = useState('connecting');

  useEffect(() => {
    const onVisibility = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    if (typeof IntersectionObserver === 'undefined') {
      return () => document.removeEventListener('visibilitychange', onVisibility);
    }
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting));
    if (container.current) observer.observe(container.current);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    if (!visible || !pageVisible) {
      setStatus('paused');
      return undefined;
    }
    let stopped = false;
    let controller;
    let timer;
    let watchdog;
    let pendingFrame;
    let drawing = false;

    async function drawLatest() {
      if (drawing) return;
      drawing = true;
      try {
        while (pendingFrame && !stopped) {
          const { blob, signal } = pendingFrame;
          pendingFrame = null;
          const bitmap = await decodeFrame(blob);
          try {
            if (!stopped && !signal.aborted && canvas.current) {
              const surface = canvas.current;
              if (surface.width !== bitmap.width) surface.width = bitmap.width;
              if (surface.height !== bitmap.height) surface.height = bitmap.height;
              surface.getContext('2d').drawImage(bitmap, 0, 0);
              setStatus('live');
            }
          } finally {
            bitmap.close?.();
          }
        }
      } catch {
        if (!stopped) {
          setStatus('error');
          controller?.abort();
        }
      } finally {
        drawing = false;
      }
    }

    async function connect() {
      if (stopped) return;
      controller = new AbortController();
      const { signal } = controller;
      const resetWatchdog = () => {
        clearTimeout(watchdog);
        watchdog = setTimeout(() => controller.abort(), 6000);
      };
      resetWatchdog();
      let retryDelay = 100;
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(getAssetUrl(`/abertura/faciais/${dispositivoId}/video/stream`), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal, cache: 'no-store',
        });
        if (!response.ok) {
          const error = new Error('Vídeo indisponível.');
          error.status = response.status;
          throw error;
        }
        let receivedFrame = false;
        await readMjpeg(response, (blob) => {
          if (stopped || signal.aborted) return;
          receivedFrame = true;
          resetWatchdog();
          pendingFrame = { blob, signal };
          drawLatest();
        });
        if (!receivedFrame) throw new Error('Transmissão encerrada sem imagens.');
      } catch (error) {
        if (stopped) return;
        setStatus('error');
        pendingFrame = null;
        controller.abort();
        if ([401, 403, 404].includes(error.status)) return;
        retryDelay = 2000;
      } finally {
        clearTimeout(watchdog);
      }
      if (!stopped) timer = setTimeout(connect, retryDelay);
    }

    setStatus('connecting');
    connect();
    return () => {
      stopped = true;
      pendingFrame = null;
      controller?.abort();
      clearTimeout(timer);
      clearTimeout(watchdog);
    };
  }, [dispositivoId, visible, pageVisible]);

  return (
    <div ref={container} className="relative mt-4 flex aspect-video items-center justify-center overflow-hidden rounded-xl bg-slate-950 text-slate-300">
      <canvas ref={canvas} role="img" aria-label={`Vídeo ao vivo de ${nome}`} className={`h-full w-full object-contain ${status === 'live' ? '' : 'hidden'}`} />
      {status !== 'live' && (
        <div role="status" className="flex flex-col items-center gap-2 px-4 text-center text-sm">
          {status === 'connecting' ? <Loader2 className="animate-spin" size={24} /> : <VideoOff size={24} />}
          {status === 'connecting' ? 'Conectando ao vídeo...' : status === 'paused' ? 'Vídeo pausado' : 'Vídeo indisponível. Verifique a câmera e o link RTSP.'}
        </div>
      )}
      {status === 'live' && <span className="absolute left-3 top-3 rounded-full bg-emerald-600 px-2.5 py-1 text-xs font-bold text-white">Ao vivo</span>}
    </div>
  );
}
